import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';
import { 
  ArrowLeft, Search, X, Crown, UserPlus, UserCheck, Loader2, Lock, Unlock
} from 'lucide-react';
import axios from 'axios';
import { API_URL } from '../config/api';

const API = `${API_URL}/api`;

export default function SearchUsers() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isDark } = useTheme();
  const { t } = useLanguage();
  
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [followingUsers, setFollowingUsers] = useState({});
  const [blockedUsers, setBlockedUsers] = useState({});

  const searchUsers = useCallback(async (searchQuery) => {
    if (!searchQuery.trim() || searchQuery.length < 2) {
      setResults([]);
      setSearched(false);
      return;
    }
    
    setLoading(true);
    setSearched(true);
    
    try {
      const res = await axios.get(`${API}/social/search`, {
        params: { q: searchQuery, limit: 30 },
        withCredentials: true
      });
      setResults(res.data?.users || []);
      
      // Track following and blocked status
      const followStatus = {};
      const blockStatus = {};
      (res.data?.users || []).forEach(u => {
        followStatus[u.user_id] = u.is_following;
        blockStatus[u.user_id] = u.is_blocked;
      });
      setFollowingUsers(followStatus);
      setBlockedUsers(blockStatus);
    } catch (err) {
      console.error('Search error:', err);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Search on query change with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (query.length >= 2) {
        searchUsers(query);
      } else {
        setResults([]);
        setSearched(false);
      }
    }, 300);
    
    return () => clearTimeout(timer);
  }, [query, searchUsers]);

  const handleFollow = async (userId) => {
    const isCurrentlyFollowing = followingUsers[userId];
    
    // Optimistic update
    setFollowingUsers(prev => ({ ...prev, [userId]: !isCurrentlyFollowing }));
    
    try {
      if (isCurrentlyFollowing) {
        await axios.post(`${API}/social/unfollow`, { user_id: userId }, { withCredentials: true });
      } else {
        await axios.post(`${API}/social/follow`, { user_id: userId }, { withCredentials: true });
      }
    } catch (err) {
      // Revert on error
      setFollowingUsers(prev => ({ ...prev, [userId]: isCurrentlyFollowing }));
      console.error('Follow error:', err);
    }
  };

  const handleToggleBlock = async (userId) => {
    const isCurrentlyBlocked = blockedUsers[userId];
    
    // Optimistic update
    setBlockedUsers(prev => ({ ...prev, [userId]: !isCurrentlyBlocked }));
    
    try {
      if (isCurrentlyBlocked) {
        await axios.post(`${API}/social/unblock`, { user_id: userId }, { withCredentials: true });
      } else {
        await axios.post(`${API}/social/block`, { user_id: userId }, { withCredentials: true });
        // Also remove from following
        setFollowingUsers(prev => ({ ...prev, [userId]: false }));
      }
    } catch (err) {
      // Revert on error
      setBlockedUsers(prev => ({ ...prev, [userId]: isCurrentlyBlocked }));
      console.error('Block toggle error:', err);
    }
  };

  return (
    <div className={`min-h-full ${isDark ? 'bg-[#0a0a0f]' : 'bg-gray-50'}`}
         style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
      {/* Header */}
      <div className={`${isDark ? 'bg-[#0a0a0f]' : 'bg-white'} border-b ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
        <div className="flex items-center gap-3 px-4 py-3">
          <button onClick={() => navigate(-1)} className="p-2 -ml-2" data-testid="back-btn">
            <ArrowLeft className={`w-5 h-5 ${isDark ? 'text-white' : 'text-gray-900'}`} />
          </button>
          
          {/* Search Input */}
          <div className={`flex-1 flex items-center gap-2 px-3 py-2 rounded-xl ${isDark ? 'bg-white/10' : 'bg-gray-100'}`}>
            <Search className={`w-5 h-5 ${isDark ? 'text-white/50' : 'text-gray-400'}`} />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('social.searchPlaceholder') || 'Cerca utenti...'}
              className={`flex-1 bg-transparent outline-none text-sm ${isDark ? 'text-white placeholder-white/40' : 'text-gray-900 placeholder-gray-400'}`}
              autoFocus
              data-testid="search-input"
            />
            {query && (
              <button onClick={() => setQuery('')} className="p-1">
                <X className={`w-4 h-4 ${isDark ? 'text-white/50' : 'text-gray-400'}`} />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 py-4">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className={`w-8 h-8 animate-spin ${isDark ? 'text-white/50' : 'text-gray-400'}`} />
          </div>
        ) : results.length > 0 ? (
          <div className="space-y-2">
            {results.map((user) => (
              <div 
                key={user.user_id}
                className={`flex items-center gap-3 p-3 rounded-xl ${
                  blockedUsers[user.user_id] 
                    ? isDark ? 'bg-red-900/20 border border-red-500/30' : 'bg-red-50 border border-red-200'
                    : isDark ? 'bg-white/5' : 'bg-white'
                }`}
              >
                {/* Avatar */}
                <button
                  onClick={() => navigate(`/app/utente/${user.user_id}`)}
                  className={`relative w-12 h-12 rounded-full overflow-hidden flex-shrink-0 ${isDark ? 'bg-white/10' : 'bg-gray-200'}`}
                >
                  {user.profile_photo ? (
                    <img src={user.profile_photo} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <span className={`text-lg font-bold ${isDark ? 'text-white/50' : 'text-gray-400'}`}>
                        {user.display_name?.charAt(0)?.toUpperCase() || '?'}
                      </span>
                    </div>
                  )}
                  {/* Blocked overlay icon */}
                  {blockedUsers[user.user_id] && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                      <Lock className="w-5 h-5 text-red-400" />
                    </div>
                  )}
                </button>

                {/* Info */}
                <button
                  onClick={() => navigate(`/app/utente/${user.user_id}`)}
                  className="flex-1 text-left"
                >
                  <div className="flex items-center gap-1">
                    {blockedUsers[user.user_id] && (
                      <Lock className="w-4 h-4 text-red-400 flex-shrink-0" />
                    )}
                    {user.is_premium && !blockedUsers[user.user_id] && (
                      <Crown className="w-4 h-4 text-amber-400 flex-shrink-0" />
                    )}
                    <span className={`font-medium ${
                      blockedUsers[user.user_id]
                        ? 'text-red-400'
                        : isDark ? 'text-white' : 'text-gray-900'
                    }`}>
                      {user.display_name}
                    </span>
                  </div>
                  {blockedUsers[user.user_id] && (
                    <p className={`text-xs mt-0.5 ${isDark ? 'text-red-400/70' : 'text-red-500'}`}>
                      Utente bloccato
                    </p>
                  )}
                </button>

                {/* Block/Unblock Button */}
                {blockedUsers[user.user_id] ? (
                  <button
                    onClick={() => handleToggleBlock(user.user_id)}
                    className="px-3 py-2 rounded-xl text-sm font-medium bg-red-500/20 text-red-400 border border-red-500/30"
                    data-testid={`unblock-btn-${user.user_id}`}
                  >
                    <Unlock className="w-4 h-4" />
                  </button>
                ) : (
                  /* Follow Button */
                  <button
                    onClick={() => handleFollow(user.user_id)}
                    className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                      followingUsers[user.user_id]
                        ? isDark ? 'bg-white/10 text-white' : 'bg-gray-200 text-gray-700'
                        : 'bg-[#E8334A] text-white'
                    }`}
                    data-testid={`follow-btn-${user.user_id}`}
                  >
                    {followingUsers[user.user_id] ? (
                      <UserCheck className="w-4 h-4" />
                    ) : (
                      <UserPlus className="w-4 h-4" />
                    )}
                  </button>
                )}
              </div>
            ))}
          </div>
        ) : searched && !loading ? (
          <div className="text-center py-12">
            <Search className={`w-12 h-12 mx-auto mb-3 ${isDark ? 'text-white/20' : 'text-gray-300'}`} />
            <p className={isDark ? 'text-white/60' : 'text-gray-500'}>
              {t('social.noResults') || 'Nessun utente trovato'}
            </p>
          </div>
        ) : (
          <div className="text-center py-12">
            <Search className={`w-12 h-12 mx-auto mb-3 ${isDark ? 'text-white/20' : 'text-gray-300'}`} />
            <p className={isDark ? 'text-white/60' : 'text-gray-500'}>
              {t('social.searchHint') || 'Cerca utenti per nome'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
